// First: the interface's text is ready before any module reads it.
import './locales'
// Then: every API call says which interface language is in use (emails follow it).
import { installApiLocaleHeader } from './lib/apiLocale'
installApiLocaleHeader()
import React from 'react'
import { applyLightStyleAtStart } from './lib/lightStyle'
import ReactDOM from 'react-dom/client'
import { StytchProvider } from '@stytch/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ApiProvider } from '@/contexts/ApiContext'
import { LicenseProvider } from '@/contexts/LicenseContext'
import App from './App'
import { ToastProvider } from '@/components/shared/Toast'
import { initSentry } from '@/lib/sentry'
import { stytchClient } from '@/lib/stytch'
// Self-hosted fonts (proxy-safe — no runtime CDN). Inter (UI), JetBrains Mono
// (source visualization) + Fira Code (general mono) via @fontsource; Charter
// (doc serif) via src/styles/fonts.css. Loaded before token/style sheets so
// --lilia-font-* resolve to real, bundled faces. See src/styles/fonts.css.
// Source Serif 4 — document prose (design handoff 2026-09-13). Paired with
// JetBrains Mono below: both sit at ~0.53em x-height, so an inline \cite{}
// mid-sentence keeps the baseline rhythm without a per-size scaling fudge.
// Charter stays in the stack behind it as the self-hosted fallback.
import '@fontsource/source-serif-4/400.css'
import '@fontsource/source-serif-4/400-italic.css'
import '@fontsource/source-serif-4/600.css'
import '@fontsource/source-serif-4/700.css'
import '@fontsource/inter/300.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
// Source pane: JetBrains Mono (structure) + Source Sans 3 (compact content —
// wide x-height, soft at 12px; Inter can look thin/harsh in S density).
import '@fontsource/jetbrains-mono/400.css'
import '@fontsource/jetbrains-mono/500.css'
import '@fontsource/source-sans-3/400.css'
import '@fontsource/source-sans-3/500.css'
import '@fontsource/fira-code/400.css'
import '@fontsource/fira-code/500.css'
import '@fontsource/fira-code/600.css'
import './styles/fonts.css'
import './index.css'
import './styles/global.scss'
// After global.scss on purpose. [data-accent='x'] and :root have the SAME
// specificity, so in light mode source order is the only thing that decides
// which wins — loaded earlier, the accent's own tokens lost the tie and every
// light accent silently shared cerulean's wash.
import './styles/accents.css'
// Last: the light styles to try, and the light before Crisp (lib/lightStyle).
import './styles/light-legacy.css'
import './styles/light-styles.css'
import './styles/scrollbars.css'
import './styles/editor.scss'

// Initialize Sentry before React renders
initSentry()
applyLightStyleAtStart()

// Catch stale-chunk errors after a deploy (old `index.html` body in
// the browser's cache, new chunk names on the server → 404 → DO
// returns the SPA fallback → browser rejects the HTML as non-JS).
// Reported 2026-05-27: plain reload() looped indefinitely because the
// cached index.html kept serving the same dead chunk hashes. Fix:
// hard reload that unregisters service workers + nukes Cache API +
// appends a cache-buster query string so intermediate caches return
// the freshest index.html.
const CHUNK_RELOAD_KEY = 'lilia:chunk-reload-attempted';

function isChunkErrorMessage(msg: string | undefined): boolean {
    if (!msg) return false;
    return (
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('is not a valid JavaScript MIME type') ||
        msg.includes("'text/html' is not a valid") ||
        msg.includes('Importing a module script failed') ||
        msg.includes('Unable to preload CSS') ||
        /Loading chunk \d+ failed/i.test(msg)
    );
}

function hardReloadForStaleChunk() {
    try {
        void (async () => {
            if ('serviceWorker' in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
            }
            if ('caches' in window) {
                const names = await caches.keys();
                await Promise.all(names.map((n) => caches.delete(n).catch(() => null)));
            }
        })();
    } catch { /* the location swap is the load-bearing step */ }
    const url = new URL(window.location.href);
    url.searchParams.set('v', String(Date.now()));
    window.location.replace(url.toString());
}

function recoverFromStaleChunkOnce() {
    try {
        if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1') return; // already tried
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    } catch { /* private mode etc. */ }
    hardReloadForStaleChunk();
}

window.addEventListener(
    'error',
    (event) => {
        if (isChunkErrorMessage(event.message)) recoverFromStaleChunkOnce();
    },
    true,
);

// `error` events fire for resource failures + module-script issues;
// unhandled promise rejections cover dynamic-import failures that
// bubble through `lazy()`.
window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as Error | undefined;
    if (reason && isChunkErrorMessage(reason.message)) recoverFromStaleChunkOnce();
});

// When the page does successfully boot, clear the guard so a future
// deploy can recover too. Deferred to a microtask so any synchronous
// import errors during this script tag's evaluation still hit the
// guard above.
queueMicrotask(() => {
    requestAnimationFrame(() => {
        try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* noop */ }
    });
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            retry: 1,
        },
        mutations: {
            onError: (error) => {
                // Global mutation error handler — individual components can override
                console.error('Mutation error:', error.message);
            },
        },
    },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <ErrorBoundary>
            <StytchProvider stytch={stytchClient}>
                <ApiProvider>
                    <LicenseProvider>
                        <QueryClientProvider client={queryClient}>
                            <ToastProvider>
                                <App />
                            </ToastProvider>
                        </QueryClientProvider>
                    </LicenseProvider>
                </ApiProvider>
            </StytchProvider>
        </ErrorBoundary>
    </React.StrictMode>,
)
